import type { HTMLAttributes, PropsWithChildren } from "react";

import type { TIconKey } from "@/ui/components/primitives/Icon";
import type { DefinedDefaults, TWithDataComponent } from "@/ui/lib/types";
import { TImageProps } from "../../primitives";

/*-------------------------------------
|  Constants
-------------------------------------*/

export const ALERT_ASSET_GRID_CLASS_MAP = {
  default: "",
  withIcon: "grid-cols-[theme(spacing.4)_1fr]",
  withImage: "grid-cols-[theme(spacing.12)_1fr]",
} as const;

export const ALERT_ORIENTATION_GRID_CLASS_MAP = {
  default: "grid-rows-[min-content_1fr]",
  reverse: "grid-rows-[1fr_min-content]",
} as const;

export const ALERT_COPY_COLUMN_CLASS_MAP = {
  default: "col-start-1",
  withAsset: "col-start-2",
} as const;

export const ALERT_VARIANT_CLASS_MAP = {
  subtle: "border-transparent bg-component-alert-subtle",
  functional: "border-transparent bg-component-alert-functional",
  reassurace: "border-transparent bg-component-alert-reassurace",
  elevated: "border-transparent bg-component-alert-elevated",
} as const;

export const ALERT_TITLE_ROW_START_CLASS_MAP: Record<TAlertOrientation, string> = {
  "default": "row-start-1",
  "reverse": "row-start-2",
};

export const ALERT_BODY_ROW_START_CLASS_MAP: Record<TAlertOrientation, string> = {
  "default": "row-start-2",
  "reverse": "row-start-1",
};

/*-------------------------------------
|  Types
-------------------------------------*/

export type TAlertVariant = keyof typeof ALERT_VARIANT_CLASS_MAP;
export type TAlertOrientation = keyof typeof ALERT_ORIENTATION_GRID_CLASS_MAP;

export type TAlertAsset = TIconKey | TImageProps;

export type TAlertAssetProps = {
  asset?: TAlertAsset;
};

export type TAlertOrientationProps = {
  orientation?: TAlertOrientation;
};

type TAlertBaseProps = TWithDataComponent & PropsWithChildren & HTMLAttributes<HTMLDivElement>;

export type TAlertWrapperProps = TAlertAssetProps & TAlertOrientationProps & TAlertBaseProps;

export type TAlertProps = {
  title: string;
  variant?: TAlertVariant;
} & TAlertWrapperProps;

export type TAlertTitleProps = Pick<TAlertProps, "title" | "asset" | "orientation">;

export type TAlertBodyProps = Pick<TAlertProps, "asset" | "orientation"> & PropsWithChildren;

type TAlertWrapperDefaultProps = DefinedDefaults<
  TAlertProps,
  "data-component"
>;

type TAlertDefaultProps = DefinedDefaults<
  TAlertProps,
  "variant" |
  "orientation"
>;

/*-------------------------------------
|  Defaults
-------------------------------------*/

export const ALERT_WRAPPER_DEFAULT_PROPS = {
  "data-component": "AlertWrapper",
} satisfies TAlertWrapperDefaultProps;

export const ALERT_DEFAULTS = {
  variant: "subtle",
  orientation: "default",
} satisfies TAlertDefaultProps;
